import { fetcher } from '#lib/backendFetcher';
import { filtersToQueryParams } from '#lib/tableFilters';

/**
 * Fetches all contact lists of the user.
 * @returns {Promise<{data: Object[], error: null}>} List of all contact lists with contact counts
 */
export const fetchAllContactLists = async () => {
	const data = await fetcher('/api/v1/contact-lists');
	const lists = data?.lists ?? [];

	const listsWithCount = lists.map((list) => ({
		...list,
		contactCount: list.contactCount ?? 0,
		companyCount: list.companyCount ?? 0,
		recipientCount: list.recipientCount ?? (list.contactCount ?? 0) + (list.companyCount ?? 0),
		emailRecipientCount: list.emailRecipientCount ?? 0,
	}));

	return { data: listsWithCount, error: null };
};

/**
 * Creates a new contact list.
 * @param {Object} listData - The data for the new contact list
 * @returns {Promise<{data: Object|null, error: null}>} The created contact list
 */
export const createContactList = async (listData) => {
	const data = await fetcher('/api/v1/contact-lists', {
		method: 'POST',
		body: listData,
	});

	return { data: data ? { ...data, contactCount: 0 } : null, error: null };
};

/**
 * Updates an existing contact list.
 * @param {Object} params - The update parameters
 * @param {string} params.id - The contact list ID
 * @param {Object} params.updates - The fields to update
 * @returns {Promise<{data: Object, error: null}>} The updated contact list
 */
export const updateContactList = async ({ id, updates }) => {
	const data = await fetcher(`/api/v1/contact-lists/${id}`, {
		method: 'PATCH',
		body: updates,
	});

	return { data, error: null };
};

/**
 * Deletes a contact list.
 * @param {string} id - The ID of the contact list to delete
 * @returns {Promise<{error: null}>} Null on success
 */
export const deleteContactList = async (id) => {
	await fetcher(`/api/v1/contact-lists/${id}`, {
		method: 'DELETE',
	});

	return { error: null };
};

/**
 * Fetches the details of a single contact list.
 * @param {string} listId - The contact list ID
 * @returns {Promise<Object|null>} The contact list details or null
 */
export const fetchContactListDetail = async (listId) => {
	if (!listId) return null;
	return fetcher(`/api/v1/contact-lists/${listId}`);
};

/**
 * Fetches one filtered page of a contact list's contacts plus the whole-list stats.
 * @param {string} listId - The contact list ID
 * @param {Object} params
 * @param {number} params.page - 1-based page
 * @param {number} params.pageSize - Rows per page (max 100)
 * @param {{search?: string, hasEmail?: boolean, hasAddress?: boolean}} [params.filters] - search matches
 *   contact name, email or company name; hasAddress = the contact's company has street + postal + city;
 *   false = has none, unset = either
 * @returns {Promise<{contacts: Object[], stats: {total: number, withEmail: number, withAddress: number},
 *   total: number, currentPage: number, pageSize: number, totalPageCount: number}>} `total` counts the
 *   filtered result, `stats` the whole list
 */
export const fetchListContactsPage = (listId, { page, pageSize, filters = {} }) => {
	const params = new URLSearchParams({ page, pageSize, ...filtersToQueryParams(filters) });
	return fetcher(`/api/v1/contact-lists/${listId}/contacts?${params}`);
};

/**
 * Fetches all contacts from multiple contact lists (deduplicated).
 * @param {string[]} listIds - Array of contact list IDs
 * @returns {Promise<{data: Object[], error: null}>} Deduplicated list of all contacts
 */
export const fetchContactsByListIds = async (listIds = []) => {
	if (!listIds.length) return { data: [], error: null };

	const allContacts = [];
	const seenIds = new Set();

	for (const listId of listIds) {
		const res = await fetcher(`/api/v1/contact-lists/${listId}/contacts`);
		const contacts = res?.contacts ?? [];
		for (const contact of contacts) {
			if (!seenIds.has(contact.id)) {
				seenIds.add(contact.id);
				allContacts.push({
					...contact,
					fullname: contact.fullName,
				});
			}
		}
	}

	return { data: allContacts, error: null };
};

/**
 * Fetches all company recipients (companies without a contact person) from multiple
 * contact lists, deduplicated by company ID. Mirrors {@link fetchContactsByListIds} for
 * the company-recipient table (`company_contactList`).
 * @param {string[]} listIds - Array of contact list IDs
 * @returns {Promise<{data: Object[], error: null}>} Deduplicated list of company recipients
 */
export const fetchCompaniesByListIds = async (listIds = []) => {
	if (!listIds.length) return { data: [], error: null };

	const allCompanies = [];
	const seenIds = new Set();

	for (const listId of listIds) {
		const res = await fetcher(`/api/v1/contact-lists/${listId}/companies`);
		const companies = res?.companies ?? [];
		for (const company of companies) {
			const crmCompany = company.crmCompanies;
			if (!crmCompany || seenIds.has(crmCompany.id)) continue;
			seenIds.add(crmCompany.id);
			allCompanies.push(crmCompany);
		}
	}

	return { data: allCompanies, error: null };
};

/**
 * Fetches the recipients of an email campaign.
 * @param {string} campaignId - The campaign ID
 * @returns {Promise<Object[]>} List of recipients
 */
export const fetchCampaignRecipients = async (campaignId) => {
	if (!campaignId) return [];
	const response = await fetcher(`/api/v1/email/campaigns/${campaignId}/recipients`);
	return response?.recipients ?? [];
};

/**
 * Searches contacts by a name search term.
 * @param {string} searchTerm - The search term (minimum 2 characters)
 * @returns {Promise<{data: Object[], error: null}>} Found contacts with fullname mapping
 */
export const searchContactsByName = async (searchTerm) => {
	if (!searchTerm || searchTerm.length < 2) {
		return { data: [], error: null };
	}

	const params = new URLSearchParams({ q: searchTerm });
	const res = await fetcher(`/api/v1/contact-lists/search-contacts?${params}`);
	const contacts = (res?.contacts ?? []).map((item) => ({
		...item,
		fullname: item.fullName,
	}));

	return { data: contacts, error: null };
};

/**
 * Adds recipients to a contact list. Pass EXACTLY ONE source — its type selects the path
 * (no source flag): crmContactIds (direct), contactIds (import then add), crmCompanyIds
 * (per-company dispatch by recipientMode), lead companies of a search query (companyIds
 * or selectAll, import then per-company dispatch).
 * @param {string} contactListId - The target contact list ID
 * @param {Object} body - Request body with exactly one of the sources below
 * @param {string[]} [body.crmContactIds] - CRM contact IDs
 * @param {string[]} [body.contactIds] - Global contact IDs (auto-imported to CRM)
 * @param {string[]} [body.companyIds] - Global company IDs of leads (max 500, needs prospectQueryId)
 * @param {true} [body.selectAll] - Every company of prospectQueryId matching body.filters
 * @param {string} [body.prospectQueryId] - Search query a lead source is resolved against
 * @param {Object} [body.filters] - Leads-table filters for selectAll
 * @param {string[]} [body.crmCompanyIds] - CRM company IDs
 * @param {'rolePriority'|'allContacts'} [body.recipientMode] - required for company sources:
 *   rolePriority = one recipient per company chosen via body.rolePriority (companies without a
 *   match are skipped); allContacts = every contact plus company fallback for companies
 *   without contacts
 * @param {string[]} [body.rolePriority] - ordered role keys (may include 'company'); required
 *   with recipientMode 'rolePriority'
 * @param {'email'|'post'} [body.reachableBy] - only add recipients reachable via this channel:
 *   'email' skips recipients without an email path, 'post' skips recipients whose company has
 *   no full postal address; omitted = no restriction
 * @returns {Promise<Object>} The result ({ addedCount, contactCount, companyCount, skippedCount })
 */
export const addContactsToList = async (contactListId, body) => {
	return fetcher(`/api/v1/contact-lists/${contactListId}/contacts`, {
		method: 'POST',
		body,
	});
};

/**
 * Previews recipient counts for a company selection WITHOUT mutating. Used by the
 * AddToContactListModal's recipient-mode step to show live counts before commit.
 *
 * Pass exactly one of crmCompanyIds or a lead source (prospectQueryId + companyIds or
 * selectAll + filters?) plus the chosen recipientMode.
 * @param {Object} body
 * @param {string[]} [body.crmCompanyIds] - CRM company IDs (no import)
 * @param {string[]} [body.companyIds] - Global lead company IDs (preview reads global
 *   tables directly — no CRM import is triggered)
 * @param {true} [body.selectAll] - Every company of prospectQueryId matching body.filters
 * @param {string} [body.prospectQueryId] - Search query a lead source is resolved against
 * @param {Object} [body.filters] - Leads-table filters for selectAll
 * @param {'rolePriority'|'allContacts'} body.recipientMode
 * @param {string[]} [body.rolePriority] - ordered role keys; required with recipientMode 'rolePriority'
 * @param {'email'|'post'} [body.reachableBy] - mirror of the add path's reachability restriction
 * @returns {Promise<{contactCount: number, companyOnlyCount: number, companyCount: number,
 *   withEmailCount: number, withAddressCount: number, withNeitherCount: number,
 *   skippedCount: number}>}
 */
export const previewContactListRecipients = async (body) => {
	return fetcher('/api/v1/contact-lists/preview-recipients', {
		method: 'POST',
		body,
	});
};

/**
 * Fetches one filtered page of a contact list's company recipients plus the whole-list stats.
 * @param {string} listId - The contact list ID
 * @param {Object} params
 * @param {number} params.page - 1-based page
 * @param {number} params.pageSize - Rows per page (max 100)
 * @param {{search?: string, hasEmail?: boolean, hasAddress?: boolean}} [params.filters] - search matches
 *   company name or email
 * @returns {Promise<{companies: Object[], stats: {total: number, withEmail: number, withAddress: number},
 *   total: number, currentPage: number, pageSize: number, totalPageCount: number}>}
 */
export const fetchListCompaniesPage = (listId, { page, pageSize, filters = {} }) => {
	const params = new URLSearchParams({ page, pageSize, ...filtersToQueryParams(filters) });
	return fetcher(`/api/v1/contact-lists/${listId}/companies?${params}`);
};

/**
 * Resolves the page of one recipient in the UNFILTERED contacts or companies tab (jump-to-row for
 * search deep links).
 * @param {Object} params
 * @param {string} params.listId - The contact list ID
 * @param {'contacts'|'companies'} params.kind - Tab the recipient lives in
 * @param {string} params.recipientId - crmContactId (contacts) or crmCompanyId (companies)
 * @param {number} params.pageSize - Rows per page the tab renders with
 * @returns {Promise<{rank: number, page: number, total: number}|null>} null when the recipient is no
 *   longer in the list (a stale link is not an error)
 */
export const fetchListRecipientPosition = async ({ listId, kind, recipientId, pageSize }) => {
	try {
		return await fetcher(
			`/api/v1/contact-lists/${listId}/${kind}/${recipientId}/position?${new URLSearchParams({ pageSize })}`,
		);
	} catch (error) {
		if (error.status === 404) return null;
		throw error;
	}
};

/**
 * Removes a scope of contacts from a contact list.
 * @param {string} listId - The contact list ID
 * @param {{ids: string[]}|{selectAll: true, filters?: Object}} scope - crmContactIds, or every
 *   contact matching the contacts-tab filters
 * @returns {Promise<{removed: number}>}
 */
export const removeContactsFromList = (listId, scope) =>
	fetcher(`/api/v1/contact-lists/${listId}/contacts/remove`, { method: 'POST', body: scope });

/**
 * Removes a scope of company recipients from a contact list.
 * @param {string} listId - The contact list ID
 * @param {{ids: string[]}|{selectAll: true, filters?: Object}} scope - crmCompanyIds, or every
 *   company recipient matching the companies-tab filters
 * @returns {Promise<{removed: number}>}
 */
export const removeCompaniesFromList = (listId, scope) =>
	fetcher(`/api/v1/contact-lists/${listId}/companies/remove`, { method: 'POST', body: scope });

/**
 * Search contacts and company recipients across all contact lists of the org by
 * name, email, company name, website and phone number.
 * @param {Object} options
 * @param {string} options.q - Search term (min 2 characters)
 * @param {number} [options.limit=10] - Max hits per entity type
 * @param {string[]} [options.listIds] - Restrict the search to these lists; omitted or empty = all lists
 * @returns {Promise<{contacts: Object[], companies: Object[]}>}
 */
export const searchContactLists = async ({ q, limit = 10, listIds }) => {
	const params = new URLSearchParams({ q, limit });
	if (listIds?.length) params.set('listIds', listIds.join(','));
	return fetcher(`/api/v1/contact-lists/search?${params}`);
};
